import { heuristicKind, roleSuggestion } from "@/lib/krav";

/** Rows that belong in the calculation: an explicit role decides; until then only rows that read as the request text itself are left out. */
export const kalkyleItems = (items) => items.filter((i) => (i.role ? i.role === "arbeid" : heuristicKind(i) !== "informasjon"));
/** Unclassified rows that read as a heading, budget or terms and still stand as «missing a price» – one quiet suggestion each, never a change. */
export const roleSuggestions = (items) => items.filter((i) => roleSuggestion(i) && ["venter_pris", "ma_vurderes"].includes(i.calc?.state)).map((i) => ({ item: i, role: roleSuggestion(i) }));

/** Same rule as the server's coverage: a post counts as priced when it carries a price or is deliberately outside the sum. */
/** Ferdig: the post is assessed and closed – priced and marked klar, included elsewhere, or dropped. Not the same as having an amount. */
export const isDone = (item) => ["klar", "inkludert", "utgar"].includes((item.calc || {}).state);
/** Priset: an amount exists – economic coverage, whatever the assessment says. */
export const hasAmount = (item) => Boolean((item.calc || {}).has_price);
/** Kept name, strict meaning: «handled» is done, never merely priced. */
export const isPriced = isDone;

/**
 * What the main table says about a post – only when it says something. The normal state (not priced yet, or priced and fine)
 * has no label at all; the dashes and the numbers carry the meaning.
 */
export function rowState(item, byId = {}) {
  const c = item.calc || {};
  const hasWork = Boolean(item.elements?.length || item.children?.length);
  if (c.state === "utgar") return { key: "utgar", label: "Utgår", quiet: true, dim: true, numbers: false };
  if (c.state === "inkludert") return { key: "inkludert", label: `Inkludert${item.included_in && byId[item.included_in] ? ` i ${byId[item.included_in].ref}` : ""}`, quiet: true, numbers: false };
  if (c.reason_code === "qty_missing") return { key: "qty", label: "Mangler mengde", warn: true, numbers: c.has_price };
  if (c.reason_code === "conflict") return { key: "conflict", label: "Motstridende kilder", warn: true, numbers: c.has_price };
  if (c.reason_code === "open_clarifications") return { key: "clar", label: "Trenger avklaring", warn: true, numbers: c.has_price };
  if (!item.price_type && hasWork) return { key: "pt", label: "Velg prisform", warn: true, numbers: c.has_price };
  if (c.reason_code === "awaiting_price") return { key: "await", label: "Venter på pris", warn: true, numbers: c.has_price };
  if (item.price_type === "opsjon") return { key: "opsjon", label: "Opsjon", quiet: true, numbers: c.has_price };
  if (item.price_type === "etter_regning") return { key: "regning", label: "Etter regning", quiet: true, numbers: c.has_price };
  if (item.price_type === "enhetspris" && !item.qty_estimated && c.has_price) return { key: "enhet", label: "Pris per enhet", quiet: true, numbers: true };
  if (c.has_price && c.state === "ma_vurderes") return { key: "vurder", label: "Ikke vurdert ferdig", quiet: true, numbers: true };
  return { key: c.has_price ? "priced" : "open", label: null, numbers: c.has_price };
}

/** Section economy for the fold head: posts, done, priced, and the in-sum cost / sale / DB when anything is priced. */
export function sectionEconomy(rows) {
  const done = rows.filter(isDone).length;
  const priced = rows.filter(hasAmount).length;
  const inSum = rows.filter((i) => i.calc?.in_sum && i.calc?.has_price);
  const cost = inSum.reduce((n, i) => n + (i.calc.cost_total || 0), 0);
  const price = inSum.reduce((n, i) => n + (i.calc.price_total || 0), 0);
  const issues = rows.filter((i) => rowState(i).warn).length;
  return { total: rows.length, done, priced, issues, cost, price, db: price - cost, dbPct: price ? ((price - cost) / price) * 100 : null, any: inSum.length > 0 };
}

export const KALKYLE_FILTERS = [["alle", "Alle"], ["uferdige", "Uferdige"], ["klar", "Ferdige"], ["avklaringer", "Avklaringer"]];
/** Precise cuts the Sammendrag opens the list with – shown as one removable chip, never as a permanent row of buttons. */
export const EXTRA_FILTERS = { mengde: "Mangler mengde", venter: "Venter på pris", prisform: "Uten prisform", simulert: "Simulert kost", regning: "Etter regning", opsjon: "Opsjoner", ikke_startet: "Ikke påbegynt" };
const hasClar = (i) => (i.clarifications || []).some((c) => c.status !== "besvart" && c.status !== "lukket") || i.calc?.reason_code === "open_clarifications";
const hasWork = (i) => Boolean(i.elements?.length || i.children?.length);
/** Legacy hints from the conversation («venter_pris», «ma_vurderes», «avklaring») all mean «show me what is left». */
export const normalizeKalkyleFilter = (f) => (!f || f === "alle" ? null : f === "klar" ? "klar" : f === "avklaringer" || f === "avklaring" ? "avklaringer" : EXTRA_FILTERS[f] ? f : "uferdige");
export function matchFilter(item, f) {
  if (!f) return true;
  const c = item.calc || {};
  switch (f) {
    case "klar": return isPriced(item);
    case "avklaringer": return hasClar(item);
    case "mengde": return (c.qty_missing || 0) > 0;
    case "venter": return (c.price_unknown || 0) > 0;
    case "prisform": return !item.price_type && hasWork(item);
    case "simulert": return (c.cost_unknown || 0) + (c.cost_assumed || 0) > 0;
    case "regning": return item.price_type === "etter_regning";
    case "opsjon": return item.price_type === "opsjon";
    case "ikke_startet": return !isPriced(item) && !hasWork(item) && !item.price_type;
    default: return !isPriced(item);
  }
}
export const filterCounts = (rows) => ({ alle: rows.length, uferdige: rows.filter((i) => !isPriced(i)).length, klar: rows.filter(isPriced).length, avklaringer: rows.filter(hasClar).length, ...Object.fromEntries(Object.keys(EXTRA_FILTERS).map((k) => [k, rows.filter((i) => matchFilter(i, k)).length])) });

/**
 * Everything the Sammendrag reads in one pass: where the calculation stands (estimert → komplett → kontrollert), what lies
 * outside the fixed-price sum, and what still stands open – each with the filter that opens it in the list.
 */
export function kalkyleOverview(items, totals = {}, quote = null) {
  const t = totals;
  const st = kalkyleStatus(items, t);
  const stage = quote && quote.status === "kontrollert" && quote.stale === false ? "kontrollert" : t.sum_kind === "complete" ? "komplett" : t.sum_kind === "preliminary" ? "estimert" : "start";
  const live = items.filter((i) => i.calc?.state !== "utgar");
  const sumOf = (rows) => { const priced = rows.filter((i) => i.calc?.has_price); return priced.length ? priced.reduce((n, i) => n + (i.calc.own_price || 0) + (i.children || []).reduce((m, c) => m + (c.calc?.in_sum ? c.calc.price_total : 0), 0), 0) : null; };
  const pick = (rows) => ({ rows, sum: sumOf(rows) });
  const outside = { regning: pick(live.filter((i) => i.price_type === "etter_regning")), opsjon: pick(live.filter((i) => i.price_type === "opsjon")), enhet: pick(live.filter((i) => i.price_type === "enhetspris" && !i.qty_estimated)) };
  const openClar = items.reduce((n, i) => n + (i.calc?.open_clarifications || 0) + (i.children || []).reduce((m, c) => m + (c.calc?.open_clarifications || 0), 0), 0);
  const counts = filterCounts(items);
  const open = [
    { key: "ikke_startet", n: counts.ikke_startet, unit: ["post ikke påbegynt", "poster ikke påbegynt"] },
    { key: "prisform", n: counts.prisform, unit: ["post uten prisform", "poster uten prisform"] },
    { key: "mengde", n: t.qty_missing || 0, unit: ["linje mangler mengde", "linjer mangler mengde"] },
    { key: "venter", n: t.price_unknown || 0, unit: ["linje venter på pris", "linjer venter på pris"] },
    { key: "avklaringer", n: openClar, unit: ["åpen avklaring", "åpne avklaringer"] },
    { key: "simulert", n: (t.cost_unknown || 0) + (t.cost_assumed || 0), unit: ["linje med simulert kost", "linjer med simulert kost"], quiet: true },
  ].filter((x) => x.n > 0);
  return { ...st, stage, outside, open, openClar, counts };
}

export const KALKYLE_SEL_KEY = (cid) => `sk.kalkyle.sel.${cid}`;

/** «Fortsett kalkylen»: the post you left unfinished, else the next one in document order that still needs you. Rows waiting to be classified are skipped. */
export function continueTarget(items, grouped, lastId, skip = {}) {
  const flat = grouped.flatMap((g) => g.rows).filter((i) => !skip[i.id]);
  const byId = Object.fromEntries(items.flatMap((i) => [i, ...(i.children || [])]).map((i) => [i.id, i]));
  const last = lastId ? byId[lastId] : null;
  const lastRoot = last ? (last.parent_id ? byId[last.parent_id] : last) : null;
  if (lastRoot && !isPriced(lastRoot) && !skip[lastRoot.id]) return { item: lastRoot, why: "der du slapp" };
  const from = lastRoot ? flat.findIndex((i) => i.id === lastRoot.id) + 1 : 0;
  const next = flat.slice(from).find((i) => !isPriced(i)) || flat.find((i) => !isPriced(i));
  return next ? { item: next, why: "neste som trenger deg" } : null;
}

/* ---- Kalkyle as a work surface: price forms in plain words, quiet row summaries, section lines, suggestions ---- */
export const PRICE_FORMS = [
  { key: "fastpris", label: "Fastpris", hint: "Én avtalt sum" },
  { key: "estimat", label: "Estimat", hint: "Foreløpig anslag" },
  { key: "etter_regning", label: "Etter regning", hint: "Faktisk forbruk" },
  { key: "enhetspris", label: "Enhetspris", hint: "Pris per stk., time eller enhet" },
];
export const PRICE_FORMS_MORE = [
  { key: "opsjon", label: "Opsjon", hint: "Tilbys som tillegg, utenfor summen" },
  { key: "inkludert", label: "Inkludert", hint: "Dekkes av en annen post" },
  { key: "utgar", label: "Utgår", hint: "Tilbys ikke" },
];

/**
 * What the row says – only when it has something to say. Not started: nothing (the number and the title carry it).
 * Started: «Påbegynt». Done: the figure and, when it matters, the price form. Something in the way: one marker.
 */
export function rowSummary(item, byId = {}) {
  const c = item.calc || {};
  const st = rowState(item, byId);
  const hasWork = Boolean(item.elements?.length || item.children?.length);
  const price = c.has_price ? (c.in_sum ? c.price_total : c.own_price ?? c.price_total) : null;
  if (st.key === "utgar" || st.key === "inkludert") return { text: st.label, tone: "quiet", dim: true };
  if (st.warn) return { text: st.label, tone: "warn", price };
  if (c.has_price) return { text: ["regning", "opsjon", "enhet", "vurder"].includes(st.key) ? st.label : null, tone: "num", price, dbPct: c.db_pct, outside: !c.in_sum };
  if (hasWork || item.price_type) return { text: "Påbegynt", tone: "quiet" };
  return { tone: "silent" };
}

/** «18 av 27 ferdig · 22 priset» – done is the measure; priced is said when it differs. */
export function sectionSummary(rows) {
  const e = sectionEconomy(rows);
  const done = e.total > 0 && e.done === e.total;
  const bits = [done ? `${e.total} av ${e.total} ferdig` : `${e.done} av ${e.total} ferdig`];
  if (!done && e.priced > e.done) bits.push(`${e.priced} priset`);
  if (e.issues) bits.push(`${e.issues} ${e.issues === 1 ? "avvik" : "avvik"}`);
  return { ...e, doneCount: e.done, done, text: bits.join(" · ") };
}

/**
 * The honest top line. «Ferdig» counts every post that is assessed and closed – priced and marked klar, included or dropped.
 * «Priset» counts posts with an amount. The fixed-price sum exists only when something is in it; posts billed by the hour
 * or offered as options are named separately, never shown as a lost zero.
 */
export function kalkyleStatus(items, totals = {}) {
  const handled = items.filter(isDone).length;
  const priced = items.filter(hasAmount).length;
  const byState = items.reduce((m, i) => { const s = i.calc?.state || "ma_vurderes"; m[s] = (m[s] || 0) + 1; return m; }, {});
  const regning = items.filter((i) => i.price_type === "etter_regning" && i.calc?.has_price).length;
  const opsjon = items.filter((i) => i.price_type === "opsjon" && i.calc?.has_price).length;
  const inSum = items.filter((i) => i.calc?.in_sum && i.calc?.has_price).length;
  const hasSum = inSum > 0 && (totals.price || 0) > 0;
  const aside = [regning ? `${regning} ${regning === 1 ? "post" : "poster"} etter regning` : null, opsjon ? `${opsjon} ${opsjon === 1 ? "opsjon" : "opsjoner"}` : null].filter(Boolean);
  return { handled, priced, byState, total: items.length, regning, opsjon, inSum, hasSum, aside, preliminary: totals.sum_kind === "preliminary" };
}

/** Stable id for a section – the no-group bucket gets a word, never a trailing dash. */
export const groupId = (key) => key || "uten-gruppe";

/** Sentence case for the SFI group names («Utstyr For Last» → «Utstyr for last»); short acronyms are left alone. */
export const humanGroup = (label) => (label || "").split(" ").map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w.length <= 3 && w === w.toUpperCase() ? w : w.toLowerCase())).join(" ");

/** Whatever concretely stops a post from counting – one line, or null. */
export function problemText(item) {
  const c = item.calc || {};
  const qtyMissing = (item.elements || []).filter((e) => e.qty_unknown || e.qty == null).length;
  const costMissing = (item.elements || []).filter((e) => e.cost_unknown || e.unit_cost == null).length;
  if (c.reason_code === "qty_missing" && qtyMissing) return `Mangler mengde på ${qtyMissing} ${qtyMissing === 1 ? "linje" : "linjer"}`;
  if (costMissing && c.reason_code !== "open_clarifications") return `Venter på pris for ${costMissing} ${costMissing === 1 ? "linje" : "linjer"}`;
  if (c.reason_code === "conflict") return "Kildene er motstridende – avgjør hvilken som gjelder";
  if (c.reason_code === "open_clarifications") return "Venter på avklaring";
  if (c.reason_code === "basis_changed") return "Kundens tekst er endret – bekreft kalkylen";
  if (c.reason_code === "basis_removed") return "Ikke med i kundens siste spesifikasjon";
  if (c.reason_code === "basis_new") return "Ny post fra kunden – ikke vurdert";
  return null;
}

const HINTS = [
  [/skutebunn|bunn|antifouling|skrog|hull|sjøkass/i, /spyl|høytrykk|mal|bunn|anode|skrog|stillas|blås/i],
  [/ventil|pumpe|rør|flens|mekan/i, /mekanik|rør|stål|arbeider/i],
  [/elektr|el-|kabel|tavle|motor/i, /elektrik|elektro/i],
  [/stål|sveis|plate|dekk/i, /stål|sveis|arbeider/i],
  [/dokk|slipp|dokking/i, /dokk|slipp|kai/i],
  [/mal|coating|overflate/i, /mal|spyl|blås/i],
  [/propell|aksel|thruster/i, /propell|mekanik|kran/i],
  [/avfall|rengj|tank|slam|sludge/i, /avfall|tømm|rengj|spyl/i],
  [/kran|løft|tung/i, /kran|lift|truck/i],
];
/** Transparent ranking of the catalog for one post: domain hints + words from the post. Never hides the catalog. */
export function suggestEntries(item, entries, limit = 4) {
  const hay = `${item.title || ""} ${item.los_text || ""} ${(item.customer_text || "").slice(0, 400)}`.toLowerCase();
  const wants = HINTS.filter(([k]) => k.test(hay)).map(([, v]) => v);
  const tokens = hay.normalize("NFD").replace(/[\u0300-\u036f]/g, "").split(/[^a-z0-9æøå]+/).filter((t) => t.length >= 5);
  return entries
    .filter((e) => e.price != null && e.status !== "historisk")
    .map((e) => { const label = `${e.label} ${e.group}`.toLowerCase(); let s = 0; wants.forEach((re) => { if (re.test(label)) s += 3; }); tokens.forEach((t) => { if (label.includes(t)) s += 2; }); if (e.group === "Timepriser" && /arbeider|mekanik/i.test(e.label)) s += 1; return [s, e]; })
    .filter(([s]) => s > 0).sort((a, b) => b[0] - a[0]).slice(0, limit).map(([, e]) => e);
}

export function matchKalkyle(item, q) {
  if (!q) return true;
  const s = q.toLowerCase();
  return [item.ref, item.wo_no, item.title, item.los_text, item.group, ...(item.elements || []).map((e) => e.description)].some((v) => (v || "").toLowerCase().includes(s));
}
